import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, Shield, Eye, EyeOff, X, Check } from 'lucide-react';

const UsersManager = ({ apiUrl, token, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, admin: 0, editor: 0, reader: 0 });

  const roleConfig = {
    admin: { label: 'Administrateur', color: '#ef4444', icon: Shield, description: 'Accès complet' },
    editor: { label: 'Éditeur', color: '#f59e0b', icon: Edit2, description: 'Lecture + Édition + Validation' },
    reader: { label: 'Lecteur', color: '#6b7280', icon: Eye, description: 'Lecture seule' }
  };

  const authenticatedFetch = async (url, options = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch(`${apiUrl}/auth/users`);
      const data = await response.json();
      setUsers(data);
      
      // Calculer les stats
      const stats = {
        total: data.length,
        admin: data.filter(u => u.role === 'admin').length,
        editor: data.filter(u => u.role === 'editor').length,
        reader: data.filter(u => u.role === 'reader').length
      };
      setStats(stats);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreate = () => {
    setSelectedUser({
      email: '',
      name: '',
      password: '',
      role: 'reader',
      isActive: true
    });
    setEditMode(true);
  };

  const handleSave = async (user) => {
    try {
      const isNew = !user._id;
      const url = isNew ? `${apiUrl}/auth/register` : `${apiUrl}/auth/users/${user._id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(user)
      });

      if (response.ok) {
        alert(isNew ? '✅ Utilisateur créé avec succès' : '✅ Utilisateur mis à jour');
        setEditMode(false);
        setSelectedUser(null);
        loadUsers();
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      alert('❌ Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${apiUrl}/auth/users/${userId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('✅ Utilisateur supprimé');
        setSelectedUser(null);
        loadUsers();
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Jamais';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
            Total
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>
            {stats.total}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
            Administrateurs
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#ef4444' }}>
            {stats.admin}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
            Éditeurs
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#f59e0b' }}>
            {stats.editor}
          </div>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
            Lecteurs
          </div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#6b7280' }}>
            {stats.reader}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginBottom: '24px'
      }}>
        <button onClick={handleCreate} className="btn-export" style={{ width: '100%' }}>
          <Plus className="icon" />
          Créer un nouvel utilisateur
        </button>
      </div>

      {/* Liste et détails */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: selectedUser || editMode ? '1fr 1fr' : '1fr',
        gap: '24px'
      }}>
        {/* Liste */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ fontSize: '18px', color: '#111827', margin: 0 }}>
              Utilisateurs ({users.length})
            </h2>
            {loading && <span style={{ fontSize: '12px', color: '#6b7280' }}>Chargement...</span>}
          </div>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {users.map(user => {
              const RoleIcon = roleConfig[user.role]?.icon || Users;
              return (
                <div
                  key={user._id}
                  onClick={() => { setSelectedUser(user); setEditMode(false); }}
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    backgroundColor: selectedUser?._id === user._id ? '#eff6ff' : 'white',
                    transition: 'background-color 0.2s',
                    borderLeft: selectedUser?._id === user._id ? '3px solid #3b82f6' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedUser?._id !== user._id) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedUser?._id !== user._id) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: roleConfig[user.role]?.color || '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '20px',
                      fontWeight: '600',
                      flexShrink: 0
                    }}>
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '4px'
                      }}>
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#111827',
                          margin: 0
                        }}>
                          {user.name}
                        </h3>
                        {user._id === currentUser._id && (
                          <span style={{
                            fontSize: '10px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '600'
                          }}>
                            VOUS
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        marginBottom: '8px'
                      }}>
                        {user.email}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          color: 'white',
                          backgroundColor: roleConfig[user.role]?.color
                        }}>
                          <RoleIcon size={12} />
                          {roleConfig[user.role]?.label}
                        </span>
                        {!user.isActive && (
                          <span style={{
                            fontSize: '11px',
                            color: '#dc2626',
                            fontWeight: '600'
                          }}>
                            INACTIF
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Détails ou formulaire */}
        {(selectedUser || editMode) && (
          editMode ? (
            <UserForm
              user={selectedUser}
              onSave={handleSave}
              onCancel={() => { setEditMode(false); setSelectedUser(null); }}
              roleConfig={roleConfig}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />
          ) : (
            <UserDetails
              user={selectedUser}
              onEdit={() => setEditMode(true)}
              onDelete={handleDelete}
              currentUser={currentUser}
              formatDate={formatDate}
              roleConfig={roleConfig}
            />
          )
        )}
      </div>
    </div>
  );
};

// Composant formulaire
const UserForm = ({ user, onSave, onCancel, roleConfig, showPassword, setShowPassword }) => {
  const [formData, setFormData] = useState({ ...user });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Le nom est obligatoire';
    }

    if (!formData.email || !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.email = 'Email invalide';
    }

    if (!user._id && (!formData.password || formData.password.length < 6)) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <h2 style={{ fontSize: '18px', color: '#111827', margin: 0 }}>
          {user._id ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        </h2>
      </div>
      <div style={{ padding: '24px', maxHeight: '600px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div className="form-group">
            <label>
              Nom complet *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Jean Dupont"
              className="inline-edit-input"
              style={{ borderColor: errors.name ? '#ef4444' : undefined }}
            />
            {errors.name && (
              <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.name}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="jean.dupont@example.com"
              className="inline-edit-input"
              style={{ borderColor: errors.email ? '#ef4444' : undefined }}
            />
            {errors.email && (
              <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.email}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>
              Mot de passe {!user._id && '*'}
              {user._id && (
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
                  (Laisser vide pour ne pas modifier)
                </span>
              )}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password || ''}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={user._id ? 'Nouveau mot de passe (optionnel)' : 'Minimum 6 caractères'}
                className="inline-edit-input"
                style={{ 
                  paddingRight: '40px',
                  borderColor: errors.password ? '#ef4444' : undefined 
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.password}
              </span>
            )}
          </div>

          <div className="form-group">
            <label>
              Rôle *
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="inline-edit-select"
            >
              {Object.entries(roleConfig).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label} - {config.description}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                style={{ width: 'auto' }}
              />
              <span>Compte actif</span>
            </label>
            <span style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
              Les comptes inactifs ne peuvent pas se connecter
            </span>
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '2px solid #e5e7eb'
          }}>
            <button onClick={handleSubmit} className="btn btn-primary" style={{ flex: 1 }}>
              <Check className="icon" />
              {user._id ? 'Enregistrer' : 'Créer'}
            </button>
            <button onClick={onCancel} className="btn btn-secondary">
              <X className="icon" />
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant détails
const UserDetails = ({ user, onEdit, onDelete, currentUser, formatDate, roleConfig }) => {
  const RoleIcon = roleConfig[user.role]?.icon || Users;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <h2 style={{ fontSize: '18px', color: '#111827', margin: 0 }}>
          Détails de l'utilisateur
        </h2>
      </div>
      <div style={{ padding: '24px', maxHeight: '600px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Avatar et nom */}
          <div style={{ textAlign: 'center', paddingBottom: '20px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: roleConfig[user.role]?.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '32px',
              fontWeight: '600',
              margin: '0 auto 16px'
            }}>
              {user.name.charAt(0).toUpperCase()}
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
              {user.name}
            </h3>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px' }}>
              {user.email}
            </p>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 16px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              backgroundColor: roleConfig[user.role]?.color
            }}>
              <RoleIcon size={16} />
              {roleConfig[user.role]?.label}
            </span>
          </div>

          {/* Informations */}
          <div className="detail-item">
            <label>Statut du compte</label>
            <p style={{ 
              color: user.isActive ? '#10b981' : '#ef4444',
              fontWeight: '600'
            }}>
              {user.isActive ? '✓ Actif' : '✗ Inactif'}
            </p>
          </div>

          <div className="detail-item">
            <label>Permissions</label>
            <p>{roleConfig[user.role]?.description}</p>
          </div>

          <div className="detail-item">
            <label>Dernière connexion</label>
            <p>{formatDate(user.lastLogin)}</p>
          </div>

          <div className="detail-item">
            <label>Compte créé le</label>
            <p>{formatDate(user.createdAt)}</p>
          </div>

          {/* Points system */}
          {user.points && (
            <div style={{
              padding: '16px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              border: '2px solid #fbbf24'
            }}>
              <label style={{ marginBottom: '12px', display: 'block' }}>⭐ Points</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#92400e' }}>Total</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#78350f' }}>
                    {user.points.total}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#92400e' }}>En attente</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                    {user.points.pending}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            paddingTop: '20px',
            borderTop: '1px solid #f3f4f6'
          }}>
            <button onClick={onEdit} className="btn btn-primary full">
              <Edit2 className="icon" />
              Modifier
            </button>

            {user._id !== currentUser._id && (
              <button
                onClick={() => onDelete(user._id)}
                className="btn btn-danger full"
              >
                <Trash2 className="icon" />
                Supprimer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersManager;